/**
 * Symmetry Commerce (https://symmetrycommerce.com)
 * Theme-Integrated Quick View
 * Custom element: <symmetry-qv-modal>
 * Uses Liquid templates for customization via theme settings
 */
if (!customElements.get('symmetry-qv-modal')) {
    class SymmetryQVModal extends HTMLElement {
      constructor() {
        super();
        this.previouslyFocused = null;
        this.activeMediaIndex = 0;
        this.mediaItems = [];
        this.mediaDrag = null;
        this.dynamicEventController = null;
        this.addButtonFeedbackTimer = null;
        this.boundHandleKeydown = this.handleKeydown.bind(this);
        this.boundHandleOpen = this.handleOpen.bind(this);
        this.autoSelectFirstAvailableVariant = this.dataset.autoSelectFirstAvailableVariant === 'true';
        this.showSwatches = this.dataset.showSwatches === 'true';
        this.swatchMap = {};
        
        // ========== WHS INTEGRATION START ==========
        this.enableWHSIntegration = this.dataset.enableWhsIntegration === 'true';
        this.whsIntegration = null;
        // ========== WHS INTEGRATION END ==========
      }
  
      connectedCallback() {
        this.dialog = this.querySelector('.symmetry-qv-dialog');
        this.content = this.querySelector('#symmetry-qv-content');
  
        // Read text settings from data attributes
        this.texts = {
          add: this.dataset.addText || 'Add to Cart',
          adding: this.dataset.addingText || 'Adding...',
          added: this.dataset.addedText || 'Added!',
          soldOut: this.dataset.soldOutText || 'Sold Out',
          unavailable: this.dataset.unavailableText || 'Unavailable',
          error: this.dataset.errorText || 'Error - Try Again'
        };
  
        // Create live region for screen reader announcements
        this.createLiveRegion();
  
        // Close triggers
        this.querySelectorAll('[data-symmetry-qv-close]').forEach(el => {
          el.addEventListener('click', () => this.close());
        });
  
        // Escape key
        document.addEventListener('keydown', this.boundHandleKeydown);
  
        // Listen for open events
        document.addEventListener('symmetry-qv:open', this.boundHandleOpen);
      }
  
      disconnectedCallback() {
        document.removeEventListener('keydown', this.boundHandleKeydown);
        document.removeEventListener('symmetry-qv:open', this.boundHandleOpen);
        this.cleanupDynamicEventBindings();
        this.clearAddButtonFeedbackTimer();
      }
  
      handleKeydown(e) {
        if (!this.isOpen) return;
  
        if (e.key === 'Escape') {
          this.close();
          return;
        }
  
        // Focus trap - only trap Tab key
        if (e.key === 'Tab') {
          this.trapFocus(e);
        }
      }
  
      trapFocus(e) {
        const focusableSelectors = [
          'button:not([disabled])',
          'a[href]',
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
          '[tabindex]:not([tabindex="-1"])'
        ].join(', ');
  
        const focusableElements = this.dialog.querySelectorAll(focusableSelectors);
        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];
  
        if (!firstFocusable) return;
  
        if (e.shiftKey) {
          // Shift + Tab: moving backwards
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          // Tab: moving forwards
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }
  
        createLiveRegion() {
        if (!this.liveRegion) {
          this.liveRegion = document.createElement('div');
          this.liveRegion.setAttribute('role', 'status');
          this.liveRegion.setAttribute('aria-live', 'polite');
          this.liveRegion.setAttribute('aria-atomic', 'true');
          this.liveRegion.className = 'symmetry-qv-sr-only';
          this.appendChild(this.liveRegion);
        }
      }
  
      announce(message) {
        if (this.liveRegion) {
          this.liveRegion.textContent = '';
          setTimeout(() => {
            this.liveRegion.textContent = message;
          }, 100);
        }
      }
  
      clearAddButtonFeedbackTimer() {
        if (this.addButtonFeedbackTimer) {
          window.clearTimeout(this.addButtonFeedbackTimer);
          this.addButtonFeedbackTimer = null;
        }
      }
  
      ensureAddButtonContent(btn) {
        let content = btn.querySelector('.symmetry-qv-add-btn__content');
        if (content) return content;
  
        const labelText = btn.textContent.trim();
        btn.textContent = '';
  
        content = document.createElement('span');
        content.className = 'symmetry-qv-add-btn__content';
  
        const spinner = document.createElement('span');
        spinner.className = 'symmetry-qv-add-btn__spinner';
        spinner.setAttribute('aria-hidden', 'true');
  
        const label = document.createElement('span');
        label.className = 'symmetry-qv-add-btn__label';
        label.textContent = labelText;
  
        const check = document.createElement('span');
        check.className = 'symmetry-qv-add-btn__check';
        check.setAttribute('aria-hidden', 'true');
        check.innerHTML = `
          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M3.5 8.5 6.5 11.5 12.5 5.5"></path>
          </svg>
        `;
  
        content.append(spinner, label, check);
        btn.appendChild(content);
  
        return content;
      }
  
      setAddButtonState(btn, label, { status = 'idle', disabled = false } = {}) {
        if (!btn) return;
  
        this.ensureAddButtonContent(btn);
  
        const labelEl = btn.querySelector('.symmetry-qv-add-btn__label');
        if (labelEl) {
          labelEl.textContent = label;
        }
  
        btn.classList.toggle('is-loading', status === 'loading');
        btn.classList.toggle('is-added', status === 'added');
        btn.classList.toggle('is-sold-out', status === 'sold-out');
        btn.disabled = disabled;
        btn.setAttribute('aria-busy', status === 'loading' ? 'true' : 'false');
      }
  
      handleOpen(e) {
        const { handle } = e.detail;

        if (!handle) return;

        const swatchScript = document.querySelector(`script[data-symmetry-qv-swatches="${handle}"]`);

        try {
          this.swatchMap = swatchScript ? JSON.parse(swatchScript.textContent) : {};
        } catch (error) {
          this.swatchMap = {};
        }

        if (handle) this.open(handle);
      }
  
      get isOpen() {
        return this.getAttribute('aria-hidden') === 'false';
      }
  
      async open(handle) {
        this.previouslyFocused = document.activeElement;
        this.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
        
        // Show loading state
        this.content.innerHTML = '<div class="symmetry-qv-loading"><span class="symmetry-qv-spinner"></span></div>';
        
        // Focus the close button immediately for focus trap
        const closeBtn = this.querySelector('.symmetry-qv-close');
        if (closeBtn) closeBtn.focus();
        
        // Fetch and render product
        await this.loadProduct(handle);
        
        // After content loads, focus the close button again (it's the first focusable element)
        if (closeBtn) closeBtn.focus();
      }
  
      close() {
        this.cleanupDynamicEventBindings();
        this.clearAddButtonFeedbackTimer();
        this.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        this.content.innerHTML = '';
        
        // Restore focus
        if (this.previouslyFocused) {
          this.previouslyFocused.focus();
        }
      }
  
      async loadProduct(handle) {
        try {
          const response = await fetch(`/products/${handle}.js`);

          if (!response.ok) {
            throw new Error(`Failed to fetch: ${response.status}`);
          }

          const product = await response.json();
          
          // ========== WHS INTEGRATION START ==========
          if (this.enableWHSIntegration && typeof window.WHSIntegration !== 'undefined') {
            product.collections = await window.WHSIntegration.fetchCollectionsFromDOM(handle);
          }
          // ========== WHS INTEGRATION END ==========
          
          await this.renderProduct(product);

        } catch (error) {
          console.error('[SymmetryQVModal] Error loading product:', error);
          this.content.innerHTML = `
            <div class="symmetry-qv-error">
              <p>Unable to load product.</p>
              <button data-symmetry-qv-close class="symmetry-qv-add-btn">Close</button>
            </div>`;
          
          // Re-bind close button
          this.content.querySelector('[data-symmetry-qv-close]')?.addEventListener('click', () => this.close());
        }
      }

      async fetchProductCollections(handle) {
        // Try to find the product card on the page to extract collection IDs
        const productCard = document.querySelector(`[data-wpd-product-handle="${handle}"]`);
        if (productCard) {
          const collectionIdsStr = productCard.getAttribute('data-wpd-product-collection-ids');
          if (collectionIdsStr) {
            return collectionIdsStr.split(',').map(id => ({ id: id.trim() }));
          }
        }
        
        // If not found, return empty array (WHS will still work, just without collection-specific pricing)
        return [];
      }
  
      async renderProduct(product) {
        const template = document.getElementById('symmetry-qv-product-template');
        if (!template) {
          console.error('[SymmetryQVModal] Product template not found, falling back to JS render');
          this.renderProductFallback(product);
          return;
        }
  
        const clone = template.content.cloneNode(true);
        // const currentVariant = product.variants[0];
        const currentVariant = this.autoSelectFirstAvailableVariant ? product.variants.find(variant => variant.available) || product.variants[0] : product.variants[0];
  
        this.product = product;
        this.currentVariant = currentVariant;
        this.mediaItems = this.getMediaItems(product);
        this.activeMediaIndex = this.getVariantMediaIndex(currentVariant);
  
        // Initialize selected options
        this.selectedOptions = {};
        product.options.forEach((option, i) => {
          const optionName = typeof option === 'string' ? option : option.name;
          this.selectedOptions[optionName] = currentVariant[`option${i + 1}`];
        });
  
        // Populate template elements FIRST (before setting up image promises)
        const titleEl = clone.querySelector('[data-qv-title]');
        if (titleEl) titleEl.textContent = product.title;
  
        const vendorEl = clone.querySelector('[data-qv-vendor]');
        if (vendorEl) vendorEl.textContent = product.vendor;
  
        const priceEl = clone.querySelector('[data-qv-price]');
        if (priceEl) {
          priceEl.innerHTML = this.getPriceSkeleton();
          priceEl.classList.add('is-loading');
        }
  
        const linkEl = clone.querySelector('[data-qv-link]');
        if (linkEl) linkEl.href = `/products/${product.handle}`;
  
        const addBtn = clone.querySelector('[data-symmetry-qv-add]');
        if (addBtn) {
          this.setAddButtonState(
            addBtn,
            currentVariant.available ? this.texts.add : this.texts.soldOut,
            { status: currentVariant.available ? 'idle' : 'sold-out', disabled: !currentVariant.available }
          );
        }
  
        // Render variants into the container
        const variantsContainer = clone.querySelector('[data-qv-variants]');
        if (variantsContainer) {
          this.renderVariantPickersIntoContainer(variantsContainer);
        }
  
        let activeSlideImage = null;
  
        const sliderTrack = clone.querySelector('[data-qv-slider-track]');
        if (sliderTrack) {
          sliderTrack.innerHTML = '';
  
          this.mediaItems.forEach((media, index) => {
            const slide = document.createElement('div');
            slide.className = 'symmetry-qv-slide' + (index === this.activeMediaIndex ? ' is-active' : '');
            slide.dataset.qvSlide = index;
            slide.setAttribute('aria-hidden', index === this.activeMediaIndex ? 'false' : 'true');
  
            const slideImg = document.createElement('img');
            slideImg.className = 'symmetry-qv-image';
            slideImg.alt = media.alt || product.title;
            slideImg.width = 600;
            slideImg.height = 600;
            slideImg.loading = index === this.activeMediaIndex ? 'eager' : 'lazy';
            slideImg.draggable = false;
  
            slideImg.src = this.getSizedImage(media.src, '600x');
            if (index === this.activeMediaIndex) {
              activeSlideImage = slideImg;
            }
            slide.appendChild(slideImg);
            sliderTrack.appendChild(slide);
          });
        }
  
        // Clear existing thumbnails and create new ones with load promises
        const thumbnailsWrapperEl = clone.querySelector('.symmetry-qv-thumbnails-wrapper');
        if (thumbnailsWrapperEl) {
          thumbnailsWrapperEl.innerHTML = '';
          
          this.mediaItems.forEach((media, index) => {
            const thumbnailBtn = document.createElement('button');
            thumbnailBtn.type = 'button';
            thumbnailBtn.className = 'symmetry-qv-thumbnail-btn' + (index === this.activeMediaIndex ? ' is-active' : '');
            thumbnailBtn.dataset.qvThumbnail = index;
            thumbnailBtn.setAttribute('aria-label', `View image ${index + 1} of ${this.mediaItems.length}`);
            thumbnailBtn.setAttribute('aria-current', index === this.activeMediaIndex ? 'true' : 'false');
            
            const thumbnailImg = document.createElement('img');
            thumbnailImg.className = 'symmetry-qv-thumbnail-img';
            thumbnailImg.alt = media.alt || product.title;
            thumbnailImg.draggable = false;
            thumbnailImg.loading = 'lazy';
            thumbnailImg.src = this.getSizedImage(media.src, '100x100');
            thumbnailBtn.appendChild(thumbnailImg);
            thumbnailsWrapperEl.appendChild(thumbnailBtn);
          });
  
          thumbnailsWrapperEl.hidden = this.mediaItems.length <= 1;
        }
  
        // Create a wrapper div to hold content while loading
        const productWrapper = document.createElement('div');
        productWrapper.className = 'symmetry-qv-product-wrapper';
        productWrapper.style.visibility = 'hidden';
        productWrapper.style.position = 'absolute';
        
        // Move all cloned content into wrapper
        while (clone.firstChild) {
          productWrapper.appendChild(clone.firstChild);
        }
  
        // Keep the loading spinner and add hidden content
        this.content.appendChild(productWrapper);
  
        // Only wait briefly for the active slide image so lazy thumbnails cannot block the modal forever.
        if (activeSlideImage) {
          await this.waitForImage(activeSlideImage, 1500);
        }
  
        // Remove loading spinner and show content
        this.content.innerHTML = '';
        productWrapper.style.visibility = '';
        productWrapper.style.position = '';
        this.content.appendChild(productWrapper);
  
        // Bind events (including thumbnail clicks)
        this.bindVariantEvents();
        this.bindMediaEvents();
        this.setActiveMedia(this.activeMediaIndex, { instant: true });

        // Update price after content is ready
        this.updatePriceDisplay(this.currentVariant);

        // ========== WHS INTEGRATION START ==========
        if (this.enableWHSIntegration) {
          this.initializeWHSIntegration();
        }
        // ========== WHS INTEGRATION END ==========
      }
  
      bindMediaEvents() {
        this.cleanupDynamicEventBindings();
  
        const controller = new AbortController();
        const { signal } = controller;
        this.dynamicEventController = controller;
  
        const thumbnails = this.content.querySelectorAll('.symmetry-qv-thumbnail-btn');
        const prevBtn = this.content.querySelector('[data-qv-media-nav="prev"]');
        const nextBtn = this.content.querySelector('[data-qv-media-nav="next"]');
        const viewport = this.content.querySelector('[data-qv-slider-viewport]');
  
        thumbnails.forEach((thumb, index) => {
          thumb.addEventListener('click', () => this.setActiveMedia(index), { signal });
          thumb.addEventListener('keydown', e => this.handleThumbnailKeydown(e, index), { signal });
        });
  
        prevBtn?.addEventListener('click', () => this.goToAdjacentMedia(-1), { signal });
        nextBtn?.addEventListener('click', () => this.goToAdjacentMedia(1), { signal });
  
        viewport?.addEventListener('keydown', e => this.handleSliderKeydown(e), { signal });
        viewport?.addEventListener('pointerdown', e => this.handlePointerDown(e), { signal });
        viewport?.addEventListener('pointermove', e => this.handlePointerMove(e), { signal });
        viewport?.addEventListener('pointerup', e => this.handlePointerEnd(e), { signal });
        viewport?.addEventListener('pointercancel', e => this.handlePointerEnd(e), { signal });
        viewport?.addEventListener('lostpointercapture', e => this.handlePointerEnd(e), { signal });
      }
  
      cleanupDynamicEventBindings() {
        this.dynamicEventController?.abort();
        this.dynamicEventController = null;
        this.mediaDrag = null;
      }
  
      waitForImage(img, timeout = 1500) {
        if (!img) return Promise.resolve();
        if (img.complete) return Promise.resolve();
  
        return new Promise(resolve => {
          let settled = false;
  
          const finalize = () => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timer);
            img.removeEventListener('load', finalize);
            img.removeEventListener('error', finalize);
            resolve();
          };
  
          const timer = window.setTimeout(finalize, timeout);
          img.addEventListener('load', finalize, { once: true });
          img.addEventListener('error', finalize, { once: true });
        });
      }
  
      getMediaItems(product) {
        const media = Array.isArray(product.media)
          ? product.media.filter(item => item?.src)
          : [];
  
        if (media.length) return media;
  
        if (product.featured_image) {
          return [{
            src: product.featured_image,
            alt: product.title
          }];
        }
  
        return [];
      }
  
      getVariantMediaIndex(variant) {
        if (!variant?.featured_image?.src || !this.mediaItems.length) return 0;
  
        const index = this.mediaItems.findIndex(media => media.src === variant.featured_image.src);
        return index >= 0 ? index : 0;
      }
  
      setActiveMedia(index, options = {}) {
        if (!this.mediaItems.length) return;
  
        const count = this.mediaItems.length;
        const normalizedIndex = ((index % count) + count) % count;
        this.activeMediaIndex = normalizedIndex;
  
        this.updateMediaPosition(options.instant ? 'instant' : 'animate');
        this.syncMediaUI(options.instant ? 'auto' : 'smooth');
      }
  
      goToAdjacentMedia(direction) {
        if (this.mediaItems.length <= 1) return;
        this.setActiveMedia(this.activeMediaIndex + direction);
      }
  
      updateMediaPosition(mode = 'animate', dragOffset = 0) {
        const track = this.content.querySelector('[data-qv-slider-track]');
        if (!track) return;
  
        track.classList.toggle('is-dragging', mode === 'drag');
        track.classList.toggle('is-instant', mode === 'instant');
        track.style.transform = `translate3d(calc(${this.activeMediaIndex * -100}% + ${dragOffset}px), 0, 0)`;
  
        if (mode === 'instant') {
          requestAnimationFrame(() => {
            track.classList.remove('is-instant');
          });
        }
      }
  
      syncMediaUI(thumbScrollBehavior = 'smooth') {
        const slides = this.content.querySelectorAll('.symmetry-qv-slide');
        const thumbnails = this.content.querySelectorAll('.symmetry-qv-thumbnail-btn');
        const prevBtn = this.content.querySelector('[data-qv-media-nav="prev"]');
        const nextBtn = this.content.querySelector('[data-qv-media-nav="next"]');
        const shouldLoop = this.mediaItems.length > 1;
  
        slides.forEach((slide, index) => {
          const isActive = index === this.activeMediaIndex;
          slide.classList.toggle('is-active', isActive);
          slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
  
        thumbnails.forEach((thumb, index) => {
          const isActive = index === this.activeMediaIndex;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-current', isActive ? 'true' : 'false');
  
          if (isActive && thumb.scrollIntoView) {
            thumb.scrollIntoView({ behavior: thumbScrollBehavior, block: 'nearest', inline: 'center' });
          }
        });
  
        if (prevBtn) {
          prevBtn.disabled = !shouldLoop && this.activeMediaIndex === 0;
          prevBtn.hidden = this.mediaItems.length <= 1;
        }
  
        if (nextBtn) {
          nextBtn.disabled = !shouldLoop && this.activeMediaIndex === this.mediaItems.length - 1;
          nextBtn.hidden = this.mediaItems.length <= 1;
        }
      }
  
      handleThumbnailKeydown(e, index) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.setActiveMedia(index + 1);
          this.content.querySelector(`.symmetry-qv-thumbnail-btn[data-qv-thumbnail="${this.activeMediaIndex}"]`)?.focus();
        }
  
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.setActiveMedia(index - 1);
          this.content.querySelector(`.symmetry-qv-thumbnail-btn[data-qv-thumbnail="${this.activeMediaIndex}"]`)?.focus();
        }
      }
  
      handleSliderKeydown(e) {
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          this.goToAdjacentMedia(1);
        }
  
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          this.goToAdjacentMedia(-1);
        }
      }
  
      handlePointerDown(e) {
        if (this.mediaItems.length <= 1) return;
  
        this.mediaDrag = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          deltaX: 0,
          deltaY: 0,
          isDragging: false,
          isLocked: false
        };
  
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }
  
      handlePointerMove(e) {
        if (!this.mediaDrag || this.mediaDrag.pointerId !== e.pointerId) return;
  
        const deltaX = e.clientX - this.mediaDrag.startX;
        const deltaY = e.clientY - this.mediaDrag.startY;
  
        this.mediaDrag.deltaX = deltaX;
        this.mediaDrag.deltaY = deltaY;
  
        if (!this.mediaDrag.isLocked) {
          if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
          this.mediaDrag.isLocked = true;
          this.mediaDrag.isDragging = Math.abs(deltaX) > Math.abs(deltaY);
        }
  
        if (!this.mediaDrag.isDragging) return;
  
        e.preventDefault();
        this.updateMediaPosition('drag', deltaX);
      }
  
      handlePointerEnd(e) {
        if (!this.mediaDrag || this.mediaDrag.pointerId !== e.pointerId) return;
  
        const viewport = this.content.querySelector('[data-qv-slider-viewport]');
        const { deltaX, isDragging } = this.mediaDrag;
        const threshold = viewport ? Math.max(48, viewport.clientWidth * 0.14) : 60;
  
        this.mediaDrag = null;
  
        if (!isDragging) {
          this.updateMediaPosition('animate');
          return;
        }
  
        if (Math.abs(deltaX) >= threshold) {
          this.goToAdjacentMedia(deltaX < 0 ? 1 : -1);
          return;
        }
  
        this.updateMediaPosition('animate');
      }
  
      renderVariantPickersIntoContainer(container) {
        // Skip if only one variant with default title
        if (this.product.variants.length === 1 && 
            this.product.variants[0].title === 'Default Title') {
          return;
        }
  
        const optionTemplate = document.getElementById('symmetry-qv-option-template');
        const btnTemplate = document.getElementById('symmetry-qv-option-btn-template');
  
        this.product.options.forEach((option, optionIndex) => {
          const optionName = typeof option === 'string' ? option : option.name;
          const values = this.getOptionValues(optionIndex);
  
          let optionEl;
          
          if (optionTemplate) {
            optionEl = optionTemplate.content.cloneNode(true).firstElementChild;
            optionEl.querySelector('[data-option-label]').textContent = optionName;
            
            const valuesContainer = optionEl.querySelector('[data-option-values]');
            values.forEach(value => {
              let btn;
              if (btnTemplate) {
                btn = btnTemplate.content.cloneNode(true).firstElementChild;
              } else {
                btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'symmetry-qv-option-btn';
              }
              const swatchInfo = this.showSwatches ? this.swatchMap[value] : null;

              if (swatchInfo) {
                btn.classList.add('symmetry-qv-option-btn--swatch');
                btn.innerHTML = `<span class="swatch" style="--swatch--background: ${swatchInfo.value}"></span><span class="visually-hidden">${value}</span>`;
              } else {
                btn.textContent = value;
              }
              btn.dataset.optionName = optionName;
              btn.dataset.optionValue = value;
              btn.setAttribute('aria-label', `${optionName}: ${value}`);
              const isSelected = this.selectedOptions[optionName] === value;
              if (isSelected) {
                btn.classList.add('is-selected');
              }
              btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
              valuesContainer.appendChild(btn);
            });

            const selectedValueEl = optionEl.querySelector('[data-option-selected-value]');
            if (selectedValueEl && this.showSwatches && this.swatchMap[this.selectedOptions[optionName]]) {
              selectedValueEl.textContent = this.selectedOptions[optionName];
            }
          } else {
            // Fallback if no template
            optionEl = document.createElement('div');
            optionEl.className = 'symmetry-qv-option';
            optionEl.innerHTML = `
              <label class="symmetry-qv-option-label">${optionName}</label>
              <div class="symmetry-qv-option-values">
                ${values.map(value => `
                  <button 
                    type="button"
                    class="symmetry-qv-option-btn ${this.selectedOptions[optionName] === value ? 'is-selected' : ''}"
                    data-option-name="${optionName}"
                    data-option-value="${value}"
                  >${(this.showSwatches && this.swatchMap[value]) ? `<span class="swatch" style="--swatch--background: ${this.swatchMap[value].value}"></span><span class="visually-hidden">${value}</span>` : value }
                  </button>
                `).join('')}
              </div>
            `;
          }
  
          container.appendChild(optionEl);
        });
      }
  
      renderProductFallback(product) {
        // Fallback JS-only rendering if template not found
        // const currentVariant = product.variants[0];
        const currentVariant = this.autoSelectFirstAvailableVariant ? product.variants.find(variant => variant.available) || product.variants[0] : product.variants[0];
  
        this.product = product;
        this.currentVariant = currentVariant;
  
        this.selectedOptions = {};
        product.options.forEach((option, i) => {
          const optionName = typeof option === 'string' ? option : option.name;
          this.selectedOptions[optionName] = currentVariant[`option${i + 1}`];
        });
  
        this.mediaItems = this.getMediaItems(product);
        this.activeMediaIndex = this.getVariantMediaIndex(currentVariant);
  
        this.content.innerHTML = `
          <div class="symmetry-qv-product">
            <div class="symmetry-qv-media">
              <div class="symmetry-qv-image-wrapper">
                <div class="symmetry-qv-slider" data-qv-slider>
                  <div class="symmetry-qv-slider-viewport" data-qv-slider-viewport tabindex="0" aria-label="Product media gallery">
                    <div class="symmetry-qv-slider-track" data-qv-slider-track></div>
                  </div>
                  <button type="button" class="symmetry-qv-media-nav symmetry-qv-media-nav--prev" data-qv-media-nav="prev" aria-label="Previous image">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="11 3 5 9 11 15"></polyline>
                    </svg>
                  </button>
                  <button type="button" class="symmetry-qv-media-nav symmetry-qv-media-nav--next" data-qv-media-nav="next" aria-label="Next image">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="2">
                      <polyline points="7 3 13 9 7 15"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
              <div class="symmetry-qv-thumbnails-container">
                <div class="symmetry-qv-thumbnails-wrapper"></div>
              </div>
            </div>
            <div class="symmetry-qv-info">
              <div class="symmetry-qv-info-top">
                <h2 id="symmetry-qv-title" class="symmetry-qv-title">${product.title}</h2>
                <div class="symmetry-qv-price is-loading" data-qv-price>${this.getPriceSkeleton()}</div>
              </div>
              <div class="symmetry-qv-info-middle">
                <div data-qv-variants class="symmetry-qv-variants"></div>
              </div>
              <div class="symmetry-qv-info-bottom">
                <button 
                  type="button" 
                  class="symmetry-qv-add-btn"
                  data-symmetry-qv-add
                  ${!currentVariant.available ? 'disabled' : ''}
                >${currentVariant.available ? this.texts.add : this.texts.soldOut}</button>
                <a href="/products/${product.handle}" class="symmetry-qv-view-full">View Full Details</a>
              </div>
            </div>
          </div>
        `;
  
        const variantsContainer = this.content.querySelector('[data-qv-variants]');
        if (variantsContainer) {
          this.renderVariantPickersIntoContainer(variantsContainer);
        }
  
        const addBtn = this.content.querySelector('[data-symmetry-qv-add]');
        if (addBtn) {
          this.setAddButtonState(
            addBtn,
            currentVariant.available ? this.texts.add : this.texts.soldOut,
            { status: currentVariant.available ? 'idle' : 'sold-out', disabled: !currentVariant.available }
          );
        }
  
        const sliderTrack = this.content.querySelector('[data-qv-slider-track]');
        if (sliderTrack) {
          sliderTrack.innerHTML = this.mediaItems.map((media, index) => `
            <div class="symmetry-qv-slide${index === this.activeMediaIndex ? ' is-active' : ''}" data-qv-slide="${index}" aria-hidden="${index === this.activeMediaIndex ? 'false' : 'true'}">
              <img
                src="${this.getSizedImage(media.src, '600x')}"
                alt="${media.alt || product.title}"
                class="symmetry-qv-image"
                width="600"
                height="600"
                loading="${index === 0 ? 'eager' : 'lazy'}"
                draggable="false"
              >
            </div>
          `).join('');
        }
  
        const thumbnailsWrapper = this.content.querySelector('.symmetry-qv-thumbnails-wrapper');
        if (thumbnailsWrapper) {
          thumbnailsWrapper.innerHTML = this.mediaItems.map((media, index) => `
            <button
              type="button"
              class="symmetry-qv-thumbnail-btn${index === this.activeMediaIndex ? ' is-active' : ''}"
              data-qv-thumbnail="${index}"
              aria-label="View image ${index + 1} of ${this.mediaItems.length}"
              aria-current="${index === this.activeMediaIndex ? 'true' : 'false'}">
              <img
                src="${this.getSizedImage(media.src, '100x100')}"
                alt="${media.alt || product.title}"
                class="symmetry-qv-thumbnail-img"
                draggable="false">
            </button>
          `).join('');
          thumbnailsWrapper.hidden = this.mediaItems.length <= 1;
        }
  
        this.bindVariantEvents();
        this.bindMediaEvents();
        this.setActiveMedia(this.activeMediaIndex, { instant: true });

        // WHS Integration: Process prices if enabled
        if (this.enableWHSIntegration) {
          this.initializeWHSIntegration();
        }
      }
  
      getOptionValues(optionIndex) {
        const key = `option${optionIndex + 1}`;
        const values = this.product.variants.map(v => v[key]);
        return [...new Set(values)];
      }
  
      bindVariantEvents() {
        // Option buttons
        this.content.querySelectorAll('.symmetry-qv-option-btn').forEach(btn => {
          btn.addEventListener('click', () => {
            const optionName = btn.dataset.optionName;
            const optionValue = btn.dataset.optionValue;
            this.selectOption(optionName, optionValue);
          });
        });
  
        // Add to cart button
        const addBtn = this.content.querySelector('[data-symmetry-qv-add]');
        if (addBtn) {
          addBtn.addEventListener('click', () => this.addToCart());
        }
  
        // Quantity buttons
        const quantityInput = this.content.querySelector('[data-qv-quantity]');
        const minusBtn = this.content.querySelector('[data-qv-quantity-minus]');
        const plusBtn = this.content.querySelector('[data-qv-quantity-plus]');
  
        if (quantityInput && minusBtn && plusBtn) {
          minusBtn.addEventListener('click', () => {
            const current = parseInt(quantityInput.value) || 1;
            if (current > 1) quantityInput.value = current - 1;
          });
          plusBtn.addEventListener('click', () => {
            const current = parseInt(quantityInput.value) || 1;
            quantityInput.value = current + 1;
          });
        }
      }
  
      selectOption(optionName, value) {
        this.selectedOptions[optionName] = value;
  
        // Always update button selected states and aria-pressed
        this.content.querySelectorAll('.symmetry-qv-option-btn').forEach(btn => {
          const isSelected = this.selectedOptions[btn.dataset.optionName] === btn.dataset.optionValue;
          btn.classList.toggle('is-selected', isSelected);
          btn.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        // Update selected value label for swatch options
        if (this.showSwatches) {
          this.content.querySelectorAll('.symmetry-qv-option').forEach(optionEl => {
            const labelEl = optionEl.querySelector('[data-option-label]');
            const selectedValueEl = optionEl.querySelector('[data-option-selected-value]');
            if (!labelEl || !selectedValueEl) return;
            const name = labelEl.textContent;
            const selected = this.selectedOptions[name];
            selectedValueEl.textContent = (selected && this.swatchMap[selected]) ? selected : '';
          });
        }
  
        // Find matching variant
        const variant = this.findVariant();
  
        if (variant) {
          this.currentVariant = variant;
          this.updateUI();
        } else {
          // No matching variant - show unavailable
          const btn = this.content.querySelector('[data-symmetry-qv-add]');
          if (btn) {
            this.clearAddButtonFeedbackTimer();
            this.setAddButtonState(btn, this.texts.unavailable, { disabled: true });
          }
        }
      }
  
      findVariant() {
        return this.product.variants.find(variant => {
          return this.product.options.every((option, index) => {
            const optionName = typeof option === 'string' ? option : option.name;
            return variant[`option${index + 1}`] === this.selectedOptions[optionName];
          });
        });
      }
  
      async addToCart() {
        const btn = this.content.querySelector('[data-symmetry-qv-add]');
        if (!btn || btn.disabled) return;
  
        this.clearAddButtonFeedbackTimer();
  
        const quantityInput = this.content.querySelector('[data-qv-quantity]');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
  
        try {
          this.setAddButtonState(btn, this.texts.adding, {
            status: 'loading',
            disabled: true
          });
  
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: this.currentVariant.id,
              quantity: quantity
            })
          });
  
          if (!response.ok) {
            throw new Error(`Failed to add to cart: ${response.status}`);
          }
  
          this.setAddButtonState(btn, this.texts.added, {
            status: 'added',
            disabled: true
          });
  
          // Announce to screen readers
          this.announce(`${this.product.title} ${this.texts.added}`);
  
          // Dispatch event for cart updates
          document.dispatchEvent(new CustomEvent('cart:added', {
            detail: {
              item: this.currentVariant,
              quantity: quantity
            }
          }));
  
          this.addButtonFeedbackTimer = window.setTimeout(() => {
            this.addButtonFeedbackTimer = null;
            this.updateUI();
          }, 1500);
  
        } catch (error) {
          console.error('[SymmetryQVModal] Add to cart error:', error);
          this.setAddButtonState(btn, this.texts.error, { disabled: true });
  
          // Announce error to screen readers
          this.announce(this.texts.error);
  
          this.addButtonFeedbackTimer = window.setTimeout(() => {
            this.addButtonFeedbackTimer = null;
            this.updateUI();
          }, 2000);
        }
      }
  
      updateUI() {
        const variant = this.currentVariant;

        // Show skeleton while updating price
        const priceEl = this.content.querySelector('[data-qv-price]');
        if (priceEl) {
          priceEl.innerHTML = this.getPriceSkeleton();
          priceEl.classList.add('is-loading');
        }

        // Update button state
        const btn = this.content.querySelector('[data-symmetry-qv-add]');
        if (btn) {
          this.clearAddButtonFeedbackTimer();
          this.setAddButtonState(
            btn,
            variant.available ? this.texts.add : this.texts.soldOut,
            { status: variant.available ? 'idle' : 'sold-out', disabled: !variant.available }
          );
        }

        // Update active media if the selected variant has a dedicated image
        if (variant.featured_image?.src) {
          this.setActiveMedia(this.getVariantMediaIndex(variant));
        }

        // Update price after a short delay to show skeleton
        setTimeout(() => {
          this.updatePriceDisplay(variant);
        }, 150);
      }
  
      formatPrice(variant) {
        let html = '';

        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const salePrice = this.formatMoney(variant.price);
          const comparePrice = this.formatMoney(variant.compare_at_price);
          
          // ========== WHS INTEGRATION START ==========
          if (this.enableWHSIntegration && typeof window.WHSIntegration !== 'undefined') {
            const integration = new window.WHSIntegration(this.product, variant);
            html += `<span class="symmetry-qv-price-sale">${integration.wrapPrice(salePrice)}</span>`;
            html += `<span class="symmetry-qv-price-compare"><s>${integration.wrapComparePrice(comparePrice)}</s></span>`;
          } else {
          // ========== WHS INTEGRATION END ==========
            html += `<span class="symmetry-qv-price-sale">${salePrice}</span>`;
            html += `<span class="symmetry-qv-price-compare"><s>${comparePrice}</s></span>`;
          // ========== WHS INTEGRATION START ==========
          }
          // ========== WHS INTEGRATION END ==========
        } else {
          const price = this.formatMoney(variant.price);
          
          // ========== WHS INTEGRATION START ==========
          if (this.enableWHSIntegration && typeof window.WHSIntegration !== 'undefined') {
            const integration = new window.WHSIntegration(this.product, variant);
            html += integration.wrapPrice(price);
          } else {
          // ========== WHS INTEGRATION END ==========
            html += price;
          // ========== WHS INTEGRATION START ==========
          }
          // ========== WHS INTEGRATION END ==========
        }

        return html;
      }

      formatMoney(cents) {
        if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
          return Shopify.formatMoney(cents);
        }
  
        const currency = (typeof Shopify !== 'undefined' && Shopify.currency?.active) || 'USD';
  
        return new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currency,
        }).format(cents / 100);
      }
  
      getSizedImage(url, size) {
        if (!url) return '';
        return url.replace(/(\.[^.]+)$/, `_${size}$1`);
      }

      getPriceSkeleton() {
        return '<span class="symmetry-qv-price-skeleton"></span>';
      }

      updatePriceDisplay(variant) {
        const priceEl = this.content.querySelector('[data-qv-price]');
        if (!priceEl) return;

        priceEl.innerHTML = this.formatPrice(variant);
        priceEl.classList.remove('is-loading');

        // ========== WHS INTEGRATION START ==========
        if (this.enableWHSIntegration && this.whsIntegration) {
          this.whsIntegration.trigger();
        }
        // ========== WHS INTEGRATION END ==========
      }

      // ========== WHS INTEGRATION START ==========
      initializeWHSIntegration() {
        if (typeof window.WHSIntegration === 'undefined') {
          console.warn('[SymmetryQVModal] WHS Integration enabled but WHSIntegration class not found');
          return;
        }
        
        this.whsIntegration = new window.WHSIntegration(this.product, this.currentVariant);
        this.whsIntegration.trigger();
      }
      // ========== WHS INTEGRATION END ==========
    }
  
    // Register custom element
    if (!customElements.get('symmetry-qv-modal')) {
      customElements.define('symmetry-qv-modal', SymmetryQVModal);
    }
  }

      // Global helper function
      window.openQuickView = function(handle) {
        document.dispatchEvent(new CustomEvent('symmetry-qv:open', { detail: { handle } }));
      };
    
      // Listen for trigger button clicks (delegated)
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest('[data-symmetry-qv-trigger]');
        if (trigger) {
          e.preventDefault();
          const handle = trigger.dataset.symmetryQvTrigger;
          window.openQuickView(handle);
        }
      });